<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import {
  consolidatedMetricMeta,
  type IConsolidatedReport,
} from '@/interfaces/entities/management'
import { consolidatedKey, useManagementStore } from '@/store/management'

const props = defineProps<{ report: IConsolidatedReport }>()
const emit = defineEmits<{ (e: 'remove'): void }>()

const store = useManagementStore()
const loadingKeys = ref<Set<string>>(new Set())

const currentMonthKey = (() => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
})()

const columns = computed(() => props.report.metrics.map((key) => consolidatedMetricMeta(key)).filter(Boolean))

const months = computed(() => {
  const now = new Date()
  const list: { key: string; label: string; isCurrent: boolean }[] = []
  for (let i = 0; i < props.report.monthsBack; i += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    list.push({ key, label: date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }), isCurrent: key === currentMonthKey })
  }
  return list
})

function rowValues(monthKey: string): Record<string, number | null> | null {
  const key = consolidatedKey(props.report.areaId, props.report.sensorId, props.report.metrics, monthKey)
  return store.consolidatedRows[key]?.values ?? null
}

const rows = computed(() =>
  months.value.map((m) => {
    const key = consolidatedKey(props.report.areaId, props.report.sensorId, props.report.metrics, m.key)
    const cached = store.consolidatedRows[key]
    return {
      ...m,
      values: cached?.values ?? null,
      complete: cached?.complete ?? false,
      loading: loadingKeys.value.has(key),
    }
  }),
)

// Totais por métrica (sum ou avg conforme metadata).
const totals = computed(() => {
  const result: Record<string, number | null> = {}
  columns.value.forEach((col) => {
    if (!col) return
    const vals = rows.value.map((r) => r.values?.[col.key]).filter((v): v is number => typeof v === 'number')
    if (!vals.length) {
      result[col.key] = null
      return
    }
    const sum = vals.reduce((a, b) => a + b, 0)
    result[col.key] = col.agg === 'avg' ? sum / vals.length : sum
  })
  return result
})

function format(value: number | null | undefined, unit: string): string {
  if (value === null || value === undefined) return '—'
  if (unit === 'R$') return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  if (unit === '') return value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ${unit}`
}

async function fetchMonth(monthKey: string, refresh: boolean) {
  const key = consolidatedKey(props.report.areaId, props.report.sensorId, props.report.metrics, monthKey)
  if (loadingKeys.value.has(key)) return
  loadingKeys.value = new Set(loadingKeys.value).add(key)
  try {
    await store.fetchReportMonth(props.report, monthKey, refresh)
  } finally {
    const next = new Set(loadingKeys.value)
    next.delete(key)
    loadingKeys.value = next
  }
}

async function loadAll(force = false) {
  for (const m of months.value) {
    const needFetch = !rowValues(m.key) || (force && m.isCurrent)
    if (!needFetch) continue
    // eslint-disable-next-line no-await-in-loop
    await fetchMonth(m.key, force && m.isCurrent)
  }
}

const isLoading = computed(() => loadingKeys.value.size > 0)
const targetLabel = computed(() =>
  props.report.sensorLabel ? `${props.report.areaLabel} › ${props.report.sensorLabel}` : props.report.areaLabel,
)

onMounted(() => loadAll())
</script>

<template>
  <div class="border border-stroke bg-white">
    <header class="flex items-center justify-between gap-2 border-b border-stroke bg-slate-50 px-4 py-2.5">
      <div class="min-w-0">
        <h3 class="truncate font-semibold text-slate-800">{{ targetLabel }}</h3>
        <p class="text-xs text-slate-400">Últimos {{ report.monthsBack }} meses</p>
      </div>
      <div class="flex shrink-0 items-center gap-2">
        <button
          type="button"
          class="border border-stroke px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          :disabled="isLoading"
          title="Recalcula o mês corrente"
          @click="loadAll(true)">
          ↻
        </button>
        <button type="button" class="border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red hover:bg-red-50" @click="emit('remove')">
          Remover
        </button>
      </div>
    </header>

    <div class="overflow-x-auto">
      <table class="w-full text-left text-sm">
        <thead class="bg-slate-50/60 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th class="px-4 py-2.5">Mês</th>
            <th v-for="col in columns" :key="col!.key" class="px-4 py-2.5 text-right">{{ col!.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.key" class="border-t border-stroke">
            <td class="px-4 py-2.5 font-medium capitalize text-slate-700">
              {{ row.label }}
              <span v-if="row.values && !row.complete" class="ml-1 text-[10px] text-amber-500">(em curso)</span>
            </td>
            <template v-if="row.loading">
              <td :colspan="columns.length" class="px-4 py-2.5 text-center text-xs text-slate-400">calculando…</td>
            </template>
            <template v-else-if="row.values">
              <td v-for="col in columns" :key="col!.key" class="px-4 py-2.5 text-right" :class="col!.key.startsWith('custo') ? 'font-bold text-primary' : 'text-slate-800'">
                {{ format(row.values[col!.key], col!.unit) }}
              </td>
            </template>
            <template v-else>
              <td :colspan="columns.length" class="px-4 py-2.5 text-center text-xs text-slate-400">—</td>
            </template>
          </tr>
        </tbody>
        <tfoot>
          <tr class="border-t-2 border-primary bg-primary/5">
            <td class="px-4 py-3 text-xs font-bold uppercase text-primary">Total / média</td>
            <td v-for="col in columns" :key="col!.key" class="px-4 py-3 text-right font-extrabold" :class="col!.key.startsWith('custo') ? 'text-primary' : 'text-slate-900'">
              {{ format(totals[col!.key], col!.unit) }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>
